import React, { useState } from 'react';
import THEME from '../utils/theme';
import SectionTag from '../components/SectionTag';
import Loader from '../components/Loader';
import useFetch from '../hooks/useFetch';
import useInView from '../hooks/useInView';
import { FaStar, FaMapMarkerAlt, FaTimes, FaBriefcase } from 'react-icons/fa';

/* ─────────────────────────────────────────────
   STORY DETAIL MODAL
───────────────────────────────────────────── */
function StoryModal({ story, onClose }) {
  if (!story) return null;

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(13,27,42,0.82)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}>
      <div style={{
        background: 'white', borderRadius: 20,
        width: '100%', maxWidth: 560,
        maxHeight: '92vh', overflowY: 'auto',
        boxShadow: '0 40px 100px rgba(0,0,0,0.40)',
        animation: 'modalIn 0.28s cubic-bezier(0.34,1.56,0.64,1)',
      }}>

        {/* ── Top banner with photo ── */}
        <div style={{
          background: `linear-gradient(135deg, ${THEME.navy} 0%, #1a2d42 100%)`,
          borderRadius: '20px 20px 0 0',
          padding: '32px 32px 28px',
          position: 'relative',
        }}>
          {/* Close */}
          <button onClick={onClose} style={{
            position: 'absolute', top: 16, right: 16,
            width: 38, height: 38, borderRadius: '50%',
            background: 'rgba(255,255,255,0.12)', border: 'none',
            color: 'white', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s, transform 0.2s',
          }}
            onMouseEnter={e => { e.currentTarget.style.background = THEME.red; e.currentTarget.style.transform = 'scale(1.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.transform = 'scale(1)'; }}>
            <FaTimes size={15} />
          </button>

          {/* Avatar + name row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            {/* Avatar — img tag */}
            <div style={{
              width: 90, height: 90, borderRadius: '50%',
              border: `4px solid ${THEME.red}70`,
              overflow: 'hidden', flexShrink: 0,
              background: THEME.red,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 8px 24px rgba(0,0,0,0.35)`,
            }}>
              {story.imageUrl ? (
                <img
                  src={story.imageUrl}
                  alt={story.name}
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: 'center top',
                    display: 'block',
                  }}
                />
              ) : (
                <span style={{
                  fontSize: 34, color: 'white', fontWeight: 700,
                  fontFamily: "'Syne', sans-serif",
                }}>
                  {story.name.charAt(0)}
                </span>
              )}
            </div>

            <div>
              <h2 style={{
                fontFamily: "'Syne', sans-serif", fontWeight: 800,
                fontSize: 22, color: 'white', marginBottom: 6, lineHeight: 1.2,
              }}>{story.name}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: `${THEME.red}35`, border: `1px solid ${THEME.red}55`,
                  color: '#FF8096', fontSize: 12, fontWeight: 600,
                  padding: '4px 12px', borderRadius: 4,
                }}>
                  <FaBriefcase size={11} /> {story.jobTitle}
                </span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 12, color: 'rgba(255,255,255,0.55)',
                }}>
                  <FaMapMarkerAlt size={11} color="rgba(255,255,255,0.55)" />
                  {story.location || 'Japan'}
                </span>
              </div>
            </div>
          </div>

          {/* Stars */}
          <div style={{ display: 'flex', gap: 5, marginTop: 20 }}>
            {[1,2,3,4,5].map(n => <FaStar key={n} size={17} color="#F5A623" />)}
          </div>
        </div>

        {/* ── Story text ── */}
        <div style={{ padding: '30px 32px 36px' }}>
          <h4 style={{
            fontFamily: "'Syne', sans-serif", fontSize: 11, fontWeight: 700,
            color: '#AAA', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16,
          }}>Their Story</h4>

          <div style={{ position: 'relative' }}>
            <span style={{
              fontFamily: "'Noto Serif', serif", fontSize: 64, lineHeight: 0.7,
              color: `${THEME.red}18`, fontWeight: 700,
              display: 'block', marginBottom: -16,
            }}>"</span>
            <p style={{
              fontFamily: "'Noto Serif', serif", fontSize: 15, lineHeight: 1.95,
              color: '#4A5568', fontStyle: 'italic',
              paddingLeft: 8,
            }}>{story.story}</p>
          </div>

          {/* CTA */}
          <div style={{
            marginTop: 30, paddingTop: 24,
            borderTop: '1px solid #EDE7DE', textAlign: 'center',
          }}>
            <p style={{ fontSize: 13, color: '#999', marginBottom: 14 }}>
              Inspired? Start your own Japan journey.
            </p>
            <a href="#apply" onClick={onClose} style={{
              display: 'inline-block',
              background: THEME.red, color: 'white',
              padding: '13px 32px', borderRadius: 8,
              fontFamily: "'Syne', sans-serif", fontWeight: 700,
              fontSize: 14, textDecoration: 'none', letterSpacing: '0.04em',
              transition: 'background 0.2s, transform 0.15s',
              boxShadow: `0 8px 24px ${THEME.red}40`,
            }}
              onMouseEnter={e => { e.currentTarget.style.background = THEME.redDark; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = THEME.red; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Apply Now →
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity:0; transform: translateY(24px) scale(0.96); }
          to   { opacity:1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────
   STORY CARD
───────────────────────────────────────────── */
function StoryCard({ story, onSelect }) {
  return (
    <div
      onClick={() => onSelect(story)}
      style={{
        background: 'white', borderRadius: 14,
        border: '1px solid #EDE7DE', position: 'relative',
        display: 'flex', flexDirection: 'column', height: '100%',
        cursor: 'pointer', overflow: 'hidden',
        transition: 'transform 0.22s, box-shadow 0.22s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.11)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}>

      {/* ── Photo banner at top ── */}
      <div style={{
        height: 200,
        flexShrink: 0,
        position: 'relative',
        background: `linear-gradient(135deg, ${THEME.navy} 0%, #1a2d42 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {story.imageUrl ? (
          <>
            {/* Background blur version */}
            <img
              src={story.imageUrl}
              alt=""
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'center',
                filter: 'blur(12px) brightness(0.4)',
                transform: 'scale(1.1)',
              }}
            />
            {/* Main portrait image */}
            <img
              src={story.imageUrl}
              alt={story.name}
              loading="lazy"
              style={{
                position: 'relative', zIndex: 1,
                width: 100, height: 100,
                borderRadius: '50%',
                objectFit: 'cover', objectPosition: 'center top',
                border: `4px solid ${THEME.red}80`,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                display: 'block',
              }}
            />
          </>
        ) : (
          <div style={{
            width: 90, height: 90, borderRadius: '50%',
            background: THEME.red,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          }}>
            <span style={{
              fontSize: 36, color: 'white', fontWeight: 700,
              fontFamily: "'Syne', sans-serif",
            }}>{story.name.charAt(0)}</span>
          </div>
        )}
      </div>

      {/* ── Card Body ── */}
      <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* Name + info */}
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <div style={{
            fontFamily: "'Syne', sans-serif", fontWeight: 700,
            fontSize: 16, color: THEME.navy, marginBottom: 4,
          }}>{story.name}</div>
          <div style={{ fontSize: 13, color: THEME.red, fontWeight: 600, marginBottom: 4 }}>
            {story.jobTitle}
          </div>
          <div style={{
            fontSize: 12, color: '#999',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            <FaMapMarkerAlt size={11} color="#999" />
            {story.location || 'Japan'}
          </div>
        </div>

        {/* Stars */}
        <div style={{ display: 'flex', gap: 3, marginBottom: 14, justifyContent: 'center' }}>
          {[1,2,3,4,5].map(n => <FaStar key={n} size={14} color="#F5A623" />)}
        </div>

        {/* Story snippet */}
        <p style={{
          fontFamily: "'Noto Serif', serif", fontSize: 13.5, lineHeight: 1.8,
          color: '#5A6475', fontStyle: 'italic', flex: 1, marginBottom: 18,
          textAlign: 'center',
        }}>
          "{story.story.length > 120 ? story.story.slice(0, 120) + '…' : story.story}"
        </p>

        {/* Button */}
        <button style={{
          width: '100%', background: 'transparent',
          border: `2px solid ${THEME.red}45`,
          color: THEME.red, borderRadius: 8,
          fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 13,
          padding: '10px 0', cursor: 'pointer', letterSpacing: '0.04em',
          transition: 'all 0.2s',
        }}
          onMouseEnter={e => {
            e.currentTarget.style.background = THEME.red;
            e.currentTarget.style.borderColor = THEME.red;
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = `${THEME.red}45`;
            e.currentTarget.style.color = THEME.red;
          }}>
          Read Full Story →
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN SECTION
───────────────────────────────────────────── */
export default function Stories() {
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const { data: stories, loading } = useFetch(`${API_BASE}/success-stories`);
  const [ref, visible] = useInView();
  const [selected, setSelected] = useState(null);
  const active = stories.filter(s => s.isActive);

  return (
    <section id="stories" style={{ padding: '100px clamp(20px,4vw,40px)', background: THEME.sand }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        <div ref={ref} className={`sk-fade-up ${visible ? 'sk-visible' : ''}`} style={{ textAlign: 'center', marginBottom: 60 }}>
          <SectionTag center>Success Stories</SectionTag>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 'clamp(2rem,4vw,3.2rem)', lineHeight: 1.1, color: THEME.navy }}>
            Their Dreams Became Reality
          </h2>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <div style={{ width: 60, height: 3, background: THEME.red, borderRadius: 2 }} />
          </div>
        </div>

        {loading ? <Loader /> : active.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 80, background: 'white', borderRadius: 14, border: '1px solid #EDE7DE' }}>
            <FaStar size={48} color={THEME.gold} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.4 }} />
            <p style={{ color: '#999' }}>Success stories coming soon</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
            gap: 28,
            alignItems: 'stretch',
          }}>
            {active.map((s, i) => (
              <div key={s._id}
                className={`sk-fade-up ${visible ? 'sk-visible' : ''}`}
                style={{ transitionDelay: `${i * 0.08}s`, display: 'flex' }}>
                <StoryCard story={s} onSelect={setSelected} />
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && <StoryModal story={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
